import {
  Button,
  Divider,
  Group,
  HoverCard,
  List,
  ListItem,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import CampaignColumnChildCell from "./CampaignColumnChildCell";
import { useMemo } from "react";
import {
  IconChevronRight,
  IconMoodEmpty,
  IconMoodSmile,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { IconMoodHappy } from "@tabler/icons-react";
import { parseDate } from "../../../../../functions/dates";

function handleConclusion(type, rate, account_type) {
  let lang = useLanguage.getState().language;
  let good = {
    smiley: (
      <ThemeIcon color="tertiary.8" opacity={0.75} variant="subtle" size={"sm"}>
        <IconMoodHappy />
      </ThemeIcon>
    ),
    color: "tertiary.8",
    text: lang.workflows.types.good_text,
  };
  let medium = {
    smiley: (
      <ThemeIcon color="orange" opacity={0.5} variant="subtle" size={"sm"}>
        <IconMoodSmile />
      </ThemeIcon>
    ),
    color: "orange",
    text: lang.workflows.types.mid_text,
  };
  let bad = {
    smiley: (
      <ThemeIcon color="red" opacity={0.5} variant="subtle" size={"sm"}>
        <IconMoodEmpty />
      </ThemeIcon>
    ),
    color: "red",
    text: lang.workflows.types.bad_text,
  };

  if (type === "contacts_opened") {
    // LinkedIn
    if (account_type === "MassMailing") {
      if (rate >= 12) {
        return good;
      } else if (rate >= 7) {
        return medium;
      }
      return bad;
    }
    // SMTP & LinkedIn
    if (rate >= 18) {
      return good;
    } else if (rate >= 10) {
      return medium;
    }
    return bad;
  }

  if (type === "contacts_clicked") {
    if (rate >= 2) {
      return good;
    } else if (rate >= 1) {
      return medium;
    }
    return bad;
  }

  if (type === "contacts_answered") {
    if (account_type === "MassMailing") {
      if (rate > 1) {
        return good;
      } else if (rate > 0.5) {
        return medium;
      }
      return bad;
    }
    if (rate >= 5) {
      return good;
    } else if (rate >= 2) {
      return medium;
    }
    return bad;
  }
}

function parseIdentifier(identifier) {
  if (identifier === "contacts_opened") {
    return "opener";
  }
  if (identifier === "contacts_clicked") {
    return "clicker";
  }
  if (identifier === "contacts_answered") {
    return "answerer";
  }
}

const no_openers_action = ["sms", "smv", "linkedin_visit"];

const ChildCell = ({ row, step, identifier }) => {
  const lang = useLanguage((s) => s.language);

  const noOpener =
    no_openers_action.includes(step.action_type) || step?.[identifier] === null;

  //#region Maths
  let rate_opener =
    step?.contacted !== null && step["contacts_opened"] !== null
      ? step.contacted > 0
        ? parseFloat(
            ((step["contacts_opened"] / step.contacted) * 100).toFixed(2),
          )
        : 0
      : null;

  const getRate = () => {
    let diviser = step?.["contacts_opened"] || step?.contacted;
    if (step?.[identifier] === null) {
      return null;
    }
    if (diviser > 0) {
      return parseFloat(((step?.[identifier] / diviser) * 100).toFixed(2));
    }
    return 0;
  };

  const getNoTracking = () => {
    if (step?.integration?.enable_tracking === false) {
      return 1;
    }
    if (step?.pool?.accounts_list?.some((x) => x.enable_tracking === false)) {
      return 2;
    }
    return false;
  };
  //#endregion

  return (
    <CampaignColumnChildCell>
      <HoverCard
        withArrow
        offset={2}
        position="top"
        disabled={getNoTracking() === false}
      >
        <HoverCard.Target>
          <Group
            justify="space-between"
            fz={13}
            component={identifier === "contacts_opened" ? undefined : Link}
            w="100%"
            h={"100%"}
            gap={"xs"}
            wrap="nowrap"
            className="statistics-button"
            px={12}
            to={`/statistics/campaigns/${row.prog_id}/workflow/${
              row.workflow.id
            }/step/${step.id}/contacts/${parseIdentifier(identifier)}/${
              step.action_type
            }?workflow_name=${row?.workflow?.name}`}
            c={"inherit"}
          >
            {noOpener ? (
              "-"
            ) : (
              <Text size="sm" fw={600}>
                {(step?.[identifier] || 0)
                  ?.toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
              </Text>
            )}

            <Text
              fw={600}
              size={"sm"}
              ml="auto"
              c={
                handleConclusion(
                  identifier,
                  identifier === "contacts_opened" ? rate_opener : getRate(),
                ).color
              }
            >
              {noOpener
                ? "-"
                : identifier === "contacts_opened"
                  ? `${(rate_opener || 0)
                      ?.toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}%`
                  : `${(getRate() || 0)
                      ?.toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}%`}
            </Text>
          </Group>
        </HoverCard.Target>

        <HoverCard.Dropdown>
          <Text size="sm">
            {getNoTracking() === 1
              ? lang.statistics.columns.rates.no_tracking_step_integration
              : getNoTracking() === 2
                ? lang.statistics.columns.rates.no_tracking_step_pool
                : ""}
          </Text>
        </HoverCard.Dropdown>
      </HoverCard>
    </CampaignColumnChildCell>
  );
};

const ABTestStats = ({ ab_tests, ab_finished_at }) => {
  const lang = useLanguage((s) => s.language);

  const bestItem = useMemo(() => {
    if (ab_finished_at) {
      let best = null;
      let bestRate = -1;

      ab_tests.forEach((item) => {
        const rate = item.count_open / item.count_total;
        if (rate > bestRate) {
          bestRate = rate;
          best = item;
        }
      });

      return best;
    }
    return null;
  }, [ab_finished_at, ab_tests]);

  return (
    <Stack w={400} mah={250} style={{ overflow: "auto" }}>
      {ab_tests.map((item) => (
        <>
          <Paper key={item.ab_subject} withBorder p={"xs"}>
            <Text>
              <b>Objet :</b> {item.ab_subject}
            </Text>
            <Group mt={4}>
              <Text>
                <b style={{ fontSize: 14 }}>{item.count_total}</b> Utilisations
              </Text>
              <Text>
                <b style={{ fontSize: 14 }}>{item.count_open}</b> Ouvertures
              </Text>
            </Group>
          </Paper>
        </>
      ))}

      {ab_finished_at && (
        <>
          <Text>
            <b>{lang.statistics.ab_test.ab_finished_at}</b>{" "}
            {parseDate(ab_finished_at)}
          </Text>
          <Text>
            <b>{lang.statistics.ab_test.choosed}</b> {bestItem?.ab_subject}
          </Text>
        </>
      )}
    </Stack>
  );
};

const ParentCell = ({ row, identifier, headerName }) => {
  const lang = useLanguage((s) => s.language);

  //#region Maths
  const contacts = row?.[identifier] === null ? " - " : row?.[identifier];

  const contacted = useMemo(() => {
    // let contacted_to_remove = 0;
    // row.steps.forEach((step) => {
    //   if (no_openers_action.includes(step.action_type)) {
    //     contacted_to_remove += step?.contacted || 0;
    //   }
    // });

    // let r = row?.contacted - contacted_to_remove;

    // if (r <= 0) {
    //   row.steps.forEach((step) => {
    //     if (!no_openers_action.includes(step.action_type)) {
    //       r += step?.contacted || 0;
    //     }
    //   });
    // }

    if (row?.contacted) {
      return row?.contacted === null ? 0 : row?.contacted;
    }
    if (row?.initial) {
      return row?.initial?.contacted === null ? 0 : row?.initial?.contacted;
    }
    let result = 0;
    row.steps.forEach((step) => {
      result += step.contacted;
    });

    return result;
  }, [row?.initial, row.steps, row?.contacted]);

  const opener = useMemo(() => {
    if (row?.contacts_opened) {
      return row?.contacts_opened === null ? 0 : row?.contacts_opened;
    }
    if (row?.initial) {
      return row?.initial?.contacts_opened;
    }
    let result = 0;
    row.steps.forEach((step) => {
      result += step.contacts_opened;
    });

    return result;
  }, [row?.initial, row.steps, row?.contacts_opened]);

  const rate_opener = useMemo(() => {
    if (row?.["contacts_opened"]) {
      return parseFloat((((opener || 0) / contacted) * 100).toFixed(2));
    }
    return null;
  }, [contacted, opener, row]);

  const rate = useMemo(() => {
    let diviser = row?.["contacts_opened"] || row?.initial?.contacted;
    if (row?.[identifier] === null) {
      return null;
    }
    if (!row.steps.find((x) => x.action_type !== "linkedin_message")) {
      diviser = row?.contacted || 0;
    }

    if (diviser > 0) {
      return parseFloat(((row[identifier] / diviser) * 100).toFixed(2));
    }
    return 0;
  }, [row, identifier]);

  const noTrackingProg = useMemo(() => {
    if (row?.initial?.integration?.enable_tracking === false) {
      return 1;
    }
    if (
      row?.initial?.pool?.accounts_list?.some(
        (x) => x.enable_tracking === false,
      )
    ) {
      return 2;
    }
    return false;
  }, [row]);
  //#endregion

  const identifierNull = useMemo(() => {
    if (row.steps.find((x) => x[identifier] !== null)) {
      return false;
    }
    return true;
  }, [identifier, row.steps]);

  if (identifierNull) {
    return "";
  }

  return (
    <HoverCard zIndex={200} openDelay={300} withArrow offset={2} position="top">
      <HoverCard.Target>
        <Button
          component={identifier === "contacts_opened" ? undefined : Link}
          w="100%"
          h={"100%"}
          gap={"xs"}
          wrap="nowrap"
          className="statistics-button"
          to={`/statistics/campaigns/${row.id}/workflow/${
            row.workflow_id
          }/contacts/${parseIdentifier(identifier)}?createdOn=${new Date(
            row?.created_on,
          ).toISOString()}&workflow_name=${row?.name}`}
          justify="space-between"
          variant="light"
          color={
            handleConclusion(
              identifier,
              identifier === "contacts_opened" ? rate_opener : rate,
            ).color
          }
          pl={4}
          pr={4}
          ml={0}
          mr={10}
          rightSection={<IconChevronRight size={18} opacity={0.5} />}
        >
          {
            handleConclusion(
              identifier,
              identifier === "contacts_opened" ? rate_opener : rate,
            ).smiley
          }
          <Text
            fw={500}
            ml={8}
            c={
              handleConclusion(
                identifier,
                identifier === "contacts_opened" ? rate_opener : rate,
              ).color
            }
          >
            {identifier === "contacts_opened"
              ? `${(rate_opener || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}%`
              : `${
                  rate?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, " ") ||
                  " - "
                }%`}
          </Text>
        </Button>
      </HoverCard.Target>

      <HoverCard.Dropdown>
        <Text maw={200}>
          {headerName} {lang.reports.unique} :{" "}
          <b>{contacts?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}</b>{" "}
          {noTrackingProg && (
            <>
              <br />
              <span
                style={{
                  opacity: 0.5,
                  fontSize: 11,
                  marginTop: 8,
                  display: "block",
                }}
              >
                {noTrackingProg === 1
                  ? lang.statistics.columns.rates.no_tracking_prog_integration
                  : noTrackingProg === 2
                    ? lang.statistics.columns.rates.no_tracking_prog_pool
                    : ""}
              </span>
            </>
          )}
        </Text>

        {identifier === "contacts_opened" && row.ab_tests.length > 0 && (
          <ABTestStats
            ab_tests={row.ab_tests}
            ab_finished_at={row.ab_finished_at}
          />
        )}
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export function CampaignColumnRates({ field, headerName, identifier }) {
  return {
    field: field,
    headerName: headerName,
    cellClassName: "statistics-button-parent",
    width: 124,
    sortable: true,
    filterable: false,
    renderCell: ({ row, value }) => {
      if (!row) {
        return;
      }
      if (row.isChild) {
        return (
          <Stack py={"xs"} w={"100%"}>
            {row.steps.map((step, index) => {
              return (
                <ChildCell
                  row={row}
                  step={step}
                  key={step.id}
                  identifier={identifier}
                />
              );
            })}
          </Stack>
        );
      }

      return (
        <ParentCell
          row={row}
          value={value}
          headerName={headerName}
          identifier={identifier}
        />
      );
    },
  };
}
